// First-party auth client for the Django-issued JWT flow.
//
// Replaces the Firebase Auth SDK. The Django backend hands us an access +
// refresh token pair; where we persist them depends on the Login form's
// remember-me checkbox:
//   * ``remember: false`` (default)  → ``sessionStorage`` — dies with the tab.
//   * ``remember: true``             → ``localStorage``   — persists until sign-out.
// Reads prefer ``localStorage`` so a remembered session wins over a stale
// sessionStorage entry left behind by an earlier tab.
//
// Wire with ``AuthContext`` (src/auth/AuthContext.jsx) so React components
// see a single ``user`` / ``signIn`` / ``signOut`` / ``register`` surface and
// don't touch these helpers directly.

import { apiClient } from './apiClient';

const ACCESS_KEY = 'writeon:access_token';
const REFRESH_KEY = 'writeon:refresh_token';
const USER_KEY = 'writeon:user';
// Legacy bucket — cleared on first access so old sessions can't masquerade.
const LEGACY_FIREBASE_KEY = 'customToken';

function sessionStore() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function localStore() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// Remember-me stashes the tokens in localStorage (persists across tabs +
// reopens); a plain login uses sessionStorage (dies with the tab). On read
// we prefer localStorage so a refresh of a remembered session wins over a
// stale sessionStorage entry left behind by a previous tab.
function readStore() {
  const ls = localStore();
  if (ls && ls.getItem(ACCESS_KEY)) return ls;
  return sessionStore();
}

function rememberedFromStorage() {
  return Boolean(localStore()?.getItem(ACCESS_KEY));
}

export function getAccessToken() {
  return readStore()?.getItem(ACCESS_KEY) || null;
}

export function getRefreshToken() {
  return readStore()?.getItem(REFRESH_KEY) || null;
}

export function getStoredUser() {
  const raw = readStore()?.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persist({ user, access_token, refresh_token }, { remember = false } = {}) {
  const target = remember ? localStore() : sessionStore();
  const other = remember ? sessionStore() : localStore();
  if (!target) return;
  if (user) target.setItem(USER_KEY, JSON.stringify(user));
  if (access_token) target.setItem(ACCESS_KEY, access_token);
  if (refresh_token) target.setItem(REFRESH_KEY, refresh_token);
  target.removeItem(LEGACY_FIREBASE_KEY);
  if (other) {
    other.removeItem(ACCESS_KEY);
    other.removeItem(REFRESH_KEY);
    other.removeItem(USER_KEY);
    other.removeItem(LEGACY_FIREBASE_KEY);
  }
}

export function clearSession() {
  for (const s of [sessionStore(), localStore()]) {
    if (!s) continue;
    s.removeItem(ACCESS_KEY);
    s.removeItem(REFRESH_KEY);
    s.removeItem(USER_KEY);
    s.removeItem(LEGACY_FIREBASE_KEY);
  }
}

export async function register({ email, password, username, fullname, locale, remember = false }) {
  const { data } = await apiClient.post(
    '/api/auth/register/',
    { email, password, username, fullname, locale },
    { headers: { 'Content-Type': 'application/json' } },
  );
  persist(data, { remember });
  return data.user;
}

// Patch user-mutable preference fields (currently just locale).
// Called from the LocaleSwitcher when an authed user toggles
// language so the User row stays in sync with the React i18n
// active locale — drives which language transactional emails go
// out in. Best-effort: a network failure here doesn't unset the
// frontend locale, just logs.
export async function updateMyPreferences(patch) {
  // BE registers the handler at ``/api/auth/me/`` (the auth module
  // owns it — same route, GET returns the user payload, PATCH
  // self-edits). Calling ``/api/me/`` silently 404s, which we
  // tolerate as "best-effort" but it means the locale-update on
  // toggle never actually reached the BE.
  const { data } = await apiClient.patch(
    '/api/auth/me/',
    patch,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function login({ email, password, remember = false }) {
  const { data } = await apiClient.post(
    '/api/auth/login/',
    { email, password },
    { headers: { 'Content-Type': 'application/json' } },
  );
  persist(data, { remember });
  return data.user;
}

export async function refreshAccessToken() {
  const refresh = getRefreshToken();
  if (!refresh) throw new Error('no_refresh_token');
  const { data } = await apiClient.post(
    '/api/auth/refresh/',
    { refresh_token: refresh },
    { headers: { 'Content-Type': 'application/json' } },
  );
  persist(
    { access_token: data.access_token, refresh_token: data.refresh_token },
    { remember: rememberedFromStorage() },
  );
  return data.access_token;
}

export async function fetchMe() {
  const { data } = await apiClient.get('/api/auth/me/');
  const s = readStore();
  if (s) s.setItem(USER_KEY, JSON.stringify(data));
  return data;
}

export async function changePassword({ current_password, new_password }) {
  const { data } = await apiClient.post(
    '/api/auth/change-password/',
    { current_password, new_password },
    { headers: { 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function requestPasswordReset({ email }) {
  const { data } = await apiClient.post(
    '/api/auth/password-reset/request/',
    { email },
    { headers: { 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function confirmPasswordReset({ email, token, new_password }) {
  const { data } = await apiClient.post(
    '/api/auth/password-reset/confirm/',
    { email, token, new_password },
    { headers: { 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function signOut() {
  try {
    await apiClient.post('/api/auth/logout/');
  } catch {
    /* best-effort; the stateless JWT means we clear locally regardless */
  }
  clearSession();
}
